import type { Options } from './wasmBridge/viewModel';
import type { PuzzleId } from '../components/luna-common';
import { autoName } from './auto-name';
import { isValidPresetOptions } from './preset';
import { validateOptions, type GameOptionDefinition } from './settings-catalog';

/** The rendering control kind for a config option (BR-66). */
export type ControlKind = 'number' | 'boolean' | 'choices';

/** Derive the control kind from a catalog definition (total over OptionType). */
export function toControlKind(definition: GameOptionDefinition): ControlKind {
  return definition.type;
}

/** One rendered config control (BR-66). Choices keep the numeric option index. */
export interface ConfigOptionView {
  definition: GameOptionDefinition;
  kind: ControlKind;
  value: number | boolean;
}

/** Build one view per exposed definition, in definition order (BR-66). */
export function buildConfigOptionViews(
  definitions: GameOptionDefinition[],
  values: Map<number, number | boolean>
): ConfigOptionView[] {
  return definitions.map((definition) => ({
    definition,
    kind: toControlKind(definition),
    value: (values.get(definition.index) ?? definition.default) as number | boolean,
  }));
}

/** Presentation-only evidence of the last custom game (BR-65). */
export interface ResumeCardData {
  label: string;
  options: Options[];
}

/**
 * Derive the resume card from the last-custom-config record. A record is valid
 * only when it covers every exposed definition and passes `validateOptions`;
 * anything else (missing, partial, malformed) yields null → card hidden (BR-65).
 */
export function deriveResumeCard(
  puzzle: PuzzleId,
  definitions: GameOptionDefinition[],
  rawOptions: Options[] | null | undefined
): ResumeCardData | null {
  if (!isValidPresetOptions(rawOptions) || rawOptions.length !== definitions.length) {
    return null;
  }

  const { valid } = validateOptions(definitions, rawOptions);
  if (!valid) {
    return null;
  }

  const label = autoName(puzzle, rawOptions, definitions);
  if (!label) {
    return null;
  }

  return { label, options: rawOptions };
}
